'use client';

import {
  locationSearchPlaceholder,
  requestLocationHotelPlaceholder,
  requestLocationHotelTitle,
  requestLocationLocationDescription,
  requestLocationPrivateAddressPlaceholder,
  requestLocationPrivateAddressTitle,
  requestLocationTravelPlaceholder,
  requestLocationTravelTitle,
  requestLocationUnknownPlaceholder,
  requestLocationUnknownTitle,
  wizardNext,
  wizardPrev,
} from '@bells/i18n';
import { AttendantSearchSingleResponse } from '@bells/schema/models/attendant';
import {
  MapSuggestSearchRequest,
  RequestLocationType,
} from '@bells/schema/models/mapbox';
import { Button } from '@bells/ui/components/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@bells/ui/components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useRequest } from '@/components/girl/request-context';
import {
  RequestFormSchema,
  type RequestForm,
} from '@bells/schema/models/request';
import {
  LocationSearchList,
  LocationSearchListRef,
} from '@bells/ui/components/location-search-list';
import { LoadingButton } from '@bells/ui/components/loading-button';
import { cn } from '@bells/core/lib/utils';
import { Input } from '@bells/ui/components/input';
import { LocationSearchInput } from '@bells/ui/components/location-search-input';

export const StepChooseAddress = ({
  data,
  resolve,
  goBack,
}: {
  data: AttendantSearchSingleResponse;
  resolve?: () => void;
  goBack?: () => void;
}) => {
  const request = useRequest();

  const form = useForm<
    Pick<RequestForm, 'address'> & {
      startDate: (string | Date)[];
    }
  >({
    resolver: zodResolver(
      RequestFormSchema.pick({
        address: true,
      })
    ),

    mode: 'all',
    defaultValues: {
      address: request.values.address,
    },
  });

  const addressWatch = form.watch('address');

  useEffect(() => {
    request.setValues({ address: addressWatch });
  }, [addressWatch]);

  useEffect(() => {
    form.resetField('address');
  }, [request.values.location]);

  const placeholder = useMemo(() => {
    if (
      request.values.location == RequestLocationType.HOTEL &&
      !request.notAvaiable
    ) {
      return requestLocationHotelPlaceholder();
    } else if (
      request.values.location == RequestLocationType.HOTEL &&
      request.notAvaiable
    ) {
      return requestLocationPrivateAddressPlaceholder();
    } else if (request.values.location == RequestLocationType.PRIVATE_ADDRESS) {
      return requestLocationPrivateAddressPlaceholder();
    } else if (request.values.location == RequestLocationType.TRAVEL) {
      return requestLocationTravelPlaceholder();
    } else if (request.values.location == RequestLocationType.UNKNOWN) {
      return requestLocationUnknownPlaceholder();
    }
  }, [request.values.location, request.notAvaiable]);

  const title = useMemo(() => {
    if (request.values.location == RequestLocationType.HOTEL) {
      return requestLocationHotelTitle();
    } else if (request.values.location == RequestLocationType.PRIVATE_ADDRESS) {
      return requestLocationPrivateAddressTitle();
    } else if (request.values.location == RequestLocationType.TRAVEL) {
      return requestLocationTravelTitle();
    } else if (request.values.location == RequestLocationType.UNKNOWN) {
      return requestLocationUnknownTitle();
    }
  }, [request.values.location]);

  const submit = form.handleSubmit((val) => {
    request.setValues(val);
    if (resolve) {
      resolve();
    }
  });

  const currentLocationType = useMemo(() => {
    return request.values.location ?? RequestLocationType.HOTEL;
  }, [request.values.location]);

  const query: MapSuggestSearchRequest | undefined = useMemo(() => {
    if (
      currentLocationType == RequestLocationType.HOTEL &&
      !request.notAvaiable
    ) {
      return {
        types: ['poi'],
        pois: ['hotel', 'hotel_bar', 'hostel'],
      };
    } else if (
      currentLocationType == RequestLocationType.HOTEL &&
      request.notAvaiable
    ) {
      return {
        types: ['address'],
      };
    } else if (currentLocationType == RequestLocationType.PRIVATE_ADDRESS) {
      return {
        types: ['address'],
      };
    } else if (currentLocationType == RequestLocationType.UNKNOWN) {
      return {
        types: ['place', 'postcode'],
      };
    } else if (currentLocationType == RequestLocationType.TRAVEL) {
      return {
        types: ['country', 'poi', 'locality', 'postcode'],
        pois: ['hotel', 'hotel_bar', 'hostel'],
        countries: [],
      };
    }
  }, [currentLocationType, request.notAvaiable]);

  const ref = useRef<LocationSearchListRef>(null);
  const [onLoading, setOnLoading] = useState<boolean>(false);
  return (
    <>
      <DialogHeader className='p-6 pb-0'>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {requestLocationLocationDescription()}
        </DialogDescription>
      </DialogHeader>
      <div className='w-full flex-1 overflow-y-auto p-4 lg:max-h-[60vh]'>
        <FormProvider {...form}>
          <form
            className='flex w-full flex-col gap-4 lg:gap-6'
            onSubmit={submit}
          >
            {request.notAvaiable && (
              <div className='flex w-full flex-col gap-4 px-2 lg:gap-6'>
                <FormField
                  onlyOnTouched
                  control={form.control}
                  name='address'
                  render={({ field }) => (
                    <FormItem className={'w-full'}>
                      <FormLabel>Addresse des Hotel</FormLabel>

                      <FormControl>
                        <LocationSearchInput
                          onLoading={setOnLoading}
                          title='Addresse des Hotel'
                          onResolve={(val) => {
                            if (val) {
                              field.onChange({
                                lng: val.lng,
                                lat: val.lat,
                                name: field?.value?.name ?? '',
                                address: `${val.name} ${val.address}`,
                                country: val.country,
                                type: currentLocationType,
                              });
                            } else {
                              field.onChange(null);
                            }
                          }}
                          query={query}
                          placeholder={requestLocationPrivateAddressPlaceholder()}
                          disabled={field.disabled}
                        ></LocationSearchInput>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  onlyOnTouched
                  control={form.control}
                  name='address'
                  render={({ field }) => (
                    <FormItem className={'w-full'}>
                      <FormLabel>Name des Hotel</FormLabel>

                      <FormControl>
                        <Input
                          {...field}
                          value={field?.value?.name ?? ''}
                          onChange={(val) =>
                            field.onChange(
                              field.value
                                ? {
                                    ...field.value,
                                    name: val.currentTarget.value,
                                  }
                                : {
                                    name: val.currentTarget.value,
                                  }
                            )
                          }
                        ></Input>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            {!request.notAvaiable && (
              <FormField
                onlyOnTouched
                control={form.control}
                name='address'
                render={({ field }) => (
                  <FormItem className={'w-full'}>
                    <FormControl>
                      <LocationSearchList
                        ref={ref}
                        onLoading={setOnLoading}
                        onResolve={(val) => {
                          if (val) {
                            field.onChange({
                              lng: val.lng,
                              lat: val.lat,
                              name: request.notAvaiable ? '' : val.name,
                              address: request.notAvaiable
                                ? `${val.name} ${val.address}`
                                : val.address,
                              country: val.country,
                              type: currentLocationType,
                            });
                          } else {
                            field.onChange(null);
                          }
                        }}
                        placeholder={placeholder}
                        query={query}
                        title={title}
                        disabled={field.disabled}
                        term={field.value?.name}
                      ></LocationSearchList>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </form>
        </FormProvider>
      </div>

      <DialogFooter className='gap-2 p-6 pt-0'>
        <div className='flex flex-1 flex-col-reverse gap-2 pt-0 sm:flex-row sm:items-center sm:justify-end sm:space-x-2'>
          {currentLocationType == RequestLocationType.HOTEL && (
            <Button
              onClick={() => {
                request.setNotAvaiable(!request.notAvaiable);
                ref.current?.reset();
              }}
              type='button'
              variant={'outline'}
            >
              {!request.notAvaiable
                ? 'Hotel nicht dabei?'
                : 'Nach Hotel suchen'}
            </Button>
          )}

          <div className='flex flex-1 flex-col-reverse gap-2 pt-0 sm:flex-row sm:justify-end sm:space-x-2'>
            <Button type='button' onClick={goBack} variant={'outline'}>
              {wizardPrev()}
            </Button>
            <LoadingButton
              type='submit'
              loading={onLoading}
              onClick={submit}
              disabled={!form.formState.isValid || onLoading}
            >
              {wizardNext()}
            </LoadingButton>
          </div>
        </div>
      </DialogFooter>
    </>
  );
};
